import { Dot, Star } from "lucide-react";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import { Badge } from "@ui/badge";
import { SVG } from "@components/items/common/SVG";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import {
  convertMsToHourMinSec,
  formatFullBookDuration,
} from "@utils/helpers/timeFormtter";
import BookMenu from "./BookMenu";
import TranslatedText from "@components/items/display/TranslatedText";
import { useLanguage } from "@modules/localizations/LanguageContext";
import {
  getCustomerQuota,
  setIsCreditModalOpen,
  useAppSelector,
} from "@lib/stores";

import { ContentDetailsParameters } from "./BookSummary";
import { useEffect, useRef, useState } from "react";
import CoinReplacmentDialog from "./CoinReplacmentDialog";
import SuccessReplacmentDialog from "./SuccessReplacmentDialog";
import { ProgressBar } from "@components/items/display/ShadcnProgressBar";
import MarkdownView from "react-showdown";
import { formatRecommended } from "@utils/helpers/textFormatter";

export interface FullBookContentDetailsParameters
  extends ContentDetailsParameters {
  salesCount: string | undefined;
  totalPageCount: string | undefined;
  year: string | undefined;
  chapters: string | undefined;
}

export type BookMenuActions = {
  onShareClick: () => void;
};

interface FullBookSummaryProps
  extends Partial<FullBookContentDetailsParameters> {
  id: string | undefined;
  title: string | undefined;
  author: string | undefined;
  durationMS: number | undefined;
  rate: number | undefined;
  progress: number | undefined;
  onListenClick: () => void;
  menu: BookMenuActions;
  isLocked: boolean | undefined;
  onReadClick?: () => void;
}

const FullBookSummary = ({
  id,
  title,
  author,
  authors,
  durationMS,
  rate,
  categories,
  thumbnail,
  listeningIcon,
  listeningTitle,
  aboutTitle,
  about,
  descriptionTitle,
  description,
  recommendedToTitle,
  recommendedTo,
  authorsTitle,
  onListenClick,
  progress,
  menu,
  isLocked,
  salesCount,
  totalPageCount,
}: FullBookSummaryProps) => {
  const { language } = useLanguage();
  const [openReplacmentDialog, setOpenReplacmentDialog] =
    useState<boolean>(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState<boolean>(false);
  const customerQuota = useAppSelector((state) => state.payment.customerQuota);

  const featuresRef = useRef<HTMLDivElement | null>(null);
  const [featuresWidth, setFeaturesWidth] = useState("auto");

  useEffect(() => {
    const updateWidth = () => {
      if (featuresRef.current) {
        setFeaturesWidth(`${featuresRef.current.offsetWidth}px`);
      }
    };

    updateWidth();

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    getCustomerQuota();
  }, []);
  return (
    <>
      <SuccessReplacmentDialog
        open={openSuccessDialog}
        setOpen={setOpenSuccessDialog}
      />
      <div className="p-5 dark:bg-[#010D19] dark:text-white">
        <div className="flex items-center justify-start">
          <div className="flex w-[270px] flex-col items-center justify-center gap-y-2">
            {thumbnail ? (
              <img
                src={getPublicMediaUrl(thumbnail)}
                alt="book"
                width={270}
                height={256}
                className="rounded-xl"
              />
            ) : (
              <div className="h-[256px] w-[270px]"></div>
            )}
            <div className="flex w-full items-center justify-start gap-x-2">
              <ProgressBar
                value={progress || 0}
                height="h-1"
                prgresserBg="bg-primary dark:bg-dark-primary"
              />
              <span className="text-[14px] text-[#B5B5B5]">{progress}%</span>
            </div>
          </div>

          <div className="flex w-full flex-col items-start justify-center gap-5 p-5">
            <div className="text-[36px] font-bold">{title}</div>
            <div className="flex w-full flex-col gap-5 text-[#727272]">
              <div className="text-[16px] font-bold">{author}</div>
              <div className="gap-x flex items-center justify-start text-[15px] font-medium">
                {categories}
              </div>
              <div className="flex items-center justify-start">
                {rate && rate >= 3 ? (
                  <>
                    <Badge className="gap-x-2 bg-[#38A7B4]/10 hover:bg-[#38A7B4]/10 dark:bg-[#D9D9D9] dark:hover:bg-[#D9D9D9]">
                      <Star
                        size={15}
                        className="fill-[#727272] text-[#727272]"
                      />
                      <div className="text-17px text-[#727272]">
                        {rate?.toFixed(1)}
                      </div>
                    </Badge>
                    <Dot />
                  </>
                ) : null}
                <div className="text-[17px] font-bold">
                  {convertMsToHourMinSec(durationMS || 0)}
                </div>
              </div>
            </div>
            <div className="mt-6 flex w-full items-center justify-start gap-x-4">
              <div className="ltr:ml-auto rtl:mr-auto">
                <BookMenu
                  onShareClick={menu.onShareClick}
                  isLocked={isLocked}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 pt-4">
          <div className="flex" style={{ width: featuresWidth }}>
            {isLocked === false ? (
              <>
                <ShadcnButton
                  className="flex w-full items-center justify-center gap-1 rounded-[10px] bg-primary px-6 py-7 text-[20px] font-medium text-white hover:bg-primary hover:opacity-85 dark:bg-[#64A6E1] dark:text-[#0C0C0C] dark:hover:bg-[#64A6E1]"
                  onClick={onListenClick}
                >
                  <div className="flex items-center justify-center gap-x-2 px-5 text-white">
                    <SVG
                      src={getPublicMediaUrl(listeningIcon)}
                      preProcessor={(code: string) =>
                        code
                          .replace(
                            /(fill=)"(?!none")[^"]+"/g,
                            'fill="currentColor"'
                          )
                          .replace(
                            /(stroke=)"(?!none")[^"]+"/g,
                            'stroke="currentColor"'
                          )
                      }
                      className="h-5 w-5 dark:text-[#010D19]"
                    />
                    <div className="dark:text-[#010D19]">{listeningTitle}</div>
                  </div>
                </ShadcnButton>
              </>
            ) : isLocked === true ? (
              customerQuota?.data?.remainingCredit !== undefined &&
              customerQuota?.data?.remainingCredit > 0 ? (
                <CoinReplacmentDialog
                  trigger={
                    <ShadcnButton
                      className="flex w-full items-center justify-center gap-1 rounded-[10px] bg-primary px-6 py-7 text-[20px] font-medium text-white hover:bg-primary hover:opacity-85 dark:bg-[#64A6E1] dark:text-[#0C0C0C] dark:hover:bg-[#64A6E1]"
                      onClick={() => setOpenReplacmentDialog(true)}
                    >
                      <SVG src="/assets/icons/coin.svg" className="h-6 w-6" />
                      <TranslatedText text="full-books.replace" />
                    </ShadcnButton>
                  }
                  open={openReplacmentDialog}
                  setOpen={setOpenReplacmentDialog}
                  setOpenSuccessDialog={setOpenSuccessDialog}
                  id={id}
                />
              ) : (
                <ShadcnButton
                  className="flex w-full items-center justify-center gap-1 rounded-[10px] bg-primary px-10 py-7 text-[18px] font-medium text-white hover:bg-primary hover:opacity-85 dark:bg-[#64A6E1] dark:text-[#0C0C0C] dark:hover:bg-[#64A6E1]"
                  onClick={() => {
                    setIsCreditModalOpen(true);
                  }}
                >
                  <SVG src="/assets/icons/coin.svg" className="h-6 w-6" />
                  <TranslatedText text="full-books.recharge" />
                </ShadcnButton>
              )
            ) : null}
          </div>

          <div className="flex w-fit items-center gap-x-4" ref={featuresRef}>
            <div className="flex w-fit flex-col items-center justify-center gap-2 rounded-xl bg-[#F3F4F7] px-6 py-4 dark:bg-[#102133]">
              <SVG src="/assets/icons/sales.svg" />
              <TranslatedText
                text="full-books.sales"
                className="text-[15px] dark:text-[#979797CC]/80"
              />

              <div className="text-[18px] font-medium">
                {parseInt(salesCount ?? "", 10).toLocaleString()}
              </div>
            </div>
            <div className="flex w-fit flex-col items-center justify-center gap-2 rounded-xl bg-[#F3F4F7] px-6 py-4 dark:bg-[#102133]">
              <SVG src="/assets/icons/book.svg" />
              <TranslatedText
                text="full-books.pages"
                className="text-[15px] dark:text-[#979797CC]/80"
              />
              <div className="text-[18px] font-medium">{totalPageCount}</div>
            </div>
            <div className="flex w-fit flex-col items-center justify-center gap-2 rounded-xl bg-[#F3F4F7] px-6 py-4 dark:bg-[#102133]">
              <SVG src="/assets/icons/timer.svg" />
              <TranslatedText
                text="full-books.listening-duration"
                className="text-[15px] dark:text-[#979797CC]/80"
              />
              <div className="text-[18px] font-medium">
                {formatFullBookDuration(durationMS ?? 0, language)}
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-8 px-8 py-5">
          <div className="space-y-2">
            <div className="text-[20px] font-bold">{descriptionTitle}</div>
            <div className="text-[16px] font-bold">{description} </div>
          </div>
          <div className="space-y-2">
            <div className="text-[20px] font-bold">{aboutTitle}</div>
            <div className="text-[16px] font-bold">{about} </div>
          </div>
          <div className="space-y-2">
            <div className="text-[20px] font-bold">{authorsTitle}</div>
            <div className="text-[16px] font-bold">{authors} </div>
          </div>
          <div className="space-y-2">
            <div className="text-[20px] font-bold">{recommendedToTitle}</div>
            <MarkdownView
              markdown={formatRecommended(recommendedTo || "")}
              options={{ tables: true, emoji: true, breaks: true, lists: true }}
              className="text-[16px] font-bold"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default FullBookSummary;
